import { Component, ViewChild, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs';
import { format, parseISO } from 'date-fns';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { DatatableComponent } from '../../../../../../../core/components/datatable/datatable.component';
import { BaseComponent } from '../../../../../../../core/shared/common/base.component';
import { TravelAdvisory } from '../../../../../../../core/models/entities';
import { environment } from '../../../../../../../../environments/environment';
import { CountryListComponent } from './services/countrylist';
import { TravelAdvisoryForm, TravelAdvisoryFormService } from './services/travel-advisory-form.service';
import { TravelAdvisoryRequest, TravelAdvisoryService } from './services/travel-advisory.service';
import DataTable from 'datatables.net-dt';

@Component({
  selector: 'app-travel-advisories',
  standalone: true,
  imports: [
    CommonModule,
    NotificationModule,
    LayoutModule,
    InputsModule,
    LabelModule,
    ButtonsModule,
    DateInputsModule,
    DialogModule,
    DropDownsModule,
    ReactiveFormsModule,
    CountryListComponent,
    DatatableComponent,
  ],
  templateUrl: './travel-advisories.component.html',
  styleUrl: './travel-advisories.component.scss',
  providers: [CountryListComponent],
})
export class TravelAdvisoriesComponent extends BaseComponent {
  @ViewChild(DatatableComponent) private table?: DatatableComponent;

  public gridService = inject(TravelAdvisoryService);
  public route = inject(ActivatedRoute);
  public notificationService = inject(NotificationService);
  private formService = inject(TravelAdvisoryFormService);
  public listData = inject(CountryListComponent);

  private baseUrl = `${environment.apiUrl}/v1/organization/administrative/traveladvisory`;
  public id!: number;

  public datatableUrl = signal<string>('');
  public formGroup = signal<FormGroup<TravelAdvisoryForm> | undefined>(undefined);
  public isNew = signal<boolean>(false);
  public isSaving = signal<boolean>(false);
  private rows = new Map<string, TravelAdvisory>();

  /** Countries currently offered by the dropdown, narrowed by handleFilter. */
  public data: Array<{ text: string; value: string }>;

  public datatableColumns = [
    { data: 'email', name: 'email', title: 'Travelers Email', type: 'string', render: DataTable.render.text() },
    // data reads the nested object the API returns; name is what travels to the
    // server in $orderby and in filters, where the relation is traversed with
    // Django's double underscore.
    {
      data: 'destination.name',
      name: 'destination__name',
      title: 'Destination',
      type: 'string',
      render: DataTable.render.text(),
    },
    {
      data: 'start_date',
      name: 'start_date',
      title: 'Trip Start Date',
      type: 'string',
      render: DataTable.render.text(),
    },
    { data: 'end_date', name: 'end_date', title: 'Trip End Date', type: 'string', render: DataTable.render.text() },
    {
      data: null,
      name: 'actions',
      title: 'Action',
      type: 'actions',
      orderable: false,
      render: (_data: unknown, _type: unknown, row: TravelAdvisory) => {
        this.rows.set(String(row.pk), row);
        return `
          <div class="d-flex gap-2">
            <button type="button" class="cyflare-btn-secondary cyflare-btn-sm action-edit" data-pk="${row.pk}">
              Edit
            </button>
            <button type="button" class="cyflare-btn-danger cyflare-btn-sm action-remove" data-pk="${row.pk}">
              Remove
            </button>
          </div>
        `;
      },
    },
  ];

  constructor() {
    super();
    this.initEffect();
    this.data = this.listData.countries.slice();
  }

  private initEffect() {
    effect(
      () => {
        this.route.queryParams.pipe(filter((params) => params['organizationId'])).subscribe((data) => {
          this.id = parseInt(data['organizationId'], 10);
          // Still the source of truth for update and delete, which build their
          // endpoint from it, even though the grid no longer reads through the
          // service.
          this.gridService.url = `${this.baseUrl}(${this.id})/`;
          this.rows.clear();
          this.datatableUrl.set(this.gridService.url);
        });
      },
      { allowSignalWrites: true }
    );
  }

  public handleFilter(value: string): void {
    this.data = this.listData.countries.filter((s) => s.text.toLowerCase().indexOf(value.toLowerCase()) !== -1);
  }

  public onTableClick(event: MouseEvent): void {
    const button = (event.target as HTMLElement).closest('button');
    const pk = button?.getAttribute('data-pk');
    if (!button || !pk) {
      return;
    }

    if (button.classList.contains('action-edit')) {
      this.openEditor(this.rows.get(pk));
    } else if (button.classList.contains('action-remove')) {
      this.remove(pk);
    }
  }

  public openEditor(advisory?: TravelAdvisory): void {
    this.isNew.set(!advisory);
    // Reset the country list, which handleFilter narrows as the user types.
    this.data = this.listData.countries.slice();
    // The date pickers work with Date objects while the model types the dates as
    // strings, so they are converted on the way in and formatted back on save.
    // destination stays as the nested object: the form service unwraps .name.
    const dataItem = advisory
      ? ({
          ...advisory,
          start_date: advisory.start_date ? parseISO(advisory.start_date) : null,
          end_date: advisory.end_date ? parseISO(advisory.end_date) : null,
        } as unknown as TravelAdvisory)
      : undefined;
    this.formGroup.set(this.formService.createTravelAdvisoryForm(dataItem));
  }

  public closeEditor(): void {
    // The dialog close button and the backdrop both land here; ignore them
    // while a save is in flight so the user cannot walk away mid-request.
    if (this.isSaving()) {
      return;
    }
    this.formGroup.set(undefined);
  }

  public save(): void {
    const formGroup = this.formGroup();
    if (!formGroup || this.isSaving()) {
      return;
    }
    const formValue = formGroup.value;
    const startDate = formValue.start_date ? new Date(formValue.start_date) : null;
    const endDate = formValue.end_date ? new Date(formValue.end_date) : null;

    const payload: TravelAdvisoryRequest = {
      organization_id: this.id,
      travel_advisory: {
        pk: this.isNew() ? null : formValue?.pk!,
        email: formValue.email!,
        destination: { name: formValue.destination || '' },
        start_date: startDate ? format(startDate, 'yyyy-MM-dd HH:mm:ss') : null!,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd HH:mm:ss') : null!,
      },
    };

    const isNew = this.isNew();
    const request = isNew
      ? this.gridService.createTravelAdvisory(payload)
      : this.gridService.updateTravelAdvisory(payload.travel_advisory);

    this.isSaving.set(true);
    request.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.closeEditor();
        this.table?.reload();
        this.showNotification(
          isNew ? 'Travel Advisory created successfully' : 'Travel Advisory updated successfully',
          'success'
        );
      },
      error: (error) => {
        this.isSaving.set(false);
        console.error(error);
        this.showNotification('Failed to save the travel advisory', 'error');
      },
    });
  }

  private remove(pk: string): void {
    this.gridService.deleteTravelAdvisory(Number(pk)).subscribe({
      next: () => {
        this.table?.reload();
        this.showNotification('Travel Advisory Deleted', 'success');
      },
      error: (error) => {
        console.error(error);
        this.showNotification('Failed to delete the travel advisory', 'error');
      },
    });
  }

  private showNotification(message: string, type: 'success' | 'error'): void {
    this.notificationService.show({
      content: message,
      type: { style: type, icon: true },
      position: { horizontal: 'center', vertical: 'top' },
    });
  }
}
